import React, { useMemo } from 'react';
import type { ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import { breakpoints } from 'src/constants/breakpoints';
import { colours } from 'src/constants/colours';
import { adsLimitForMap } from 'src/constants/googleMap';
import { styledComponent } from 'src/utils/styled';
import OrderBySelect from '../../OrderBySelect';

const Container = styledComponent(Box)`
    background: ${colours.primary.O04};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0.5rem 2rem 0.5rem 2rem;
    @media (width < ${breakpoints.desktopLarge}px) {
        padding: 0.5rem 1rem 0.5rem 1rem;
    }
    @media (width < ${breakpoints.desktopSmall}px) {
        padding: 0.5rem 2rem 0.5rem 2rem;
    }
    @media (width < ${breakpoints.tabletPortrait}px) {
        padding: 0.5rem 1rem 0.5rem 1rem;
    }
`;

const StyledAdCountTypography = styledComponent(Typography)`
    color: ${colours.linkGrey};
    font-weight: 400;
    font-size: 0.875rem;
`;

const StyledListIconButton = styledComponent(IconButton)`
    margin-left: 0.5rem;
    margin-right: 0.5rem;
`;

type Props = {
    totalAds?: number;
    viewMode: 'map' | 'list';
    isHybridView: boolean;
    handleToggleViewMode: () => void;
    handleOrderChange: (orderBy: string) => void;
};

export const TotalAdsAndViewModeAndOrder: React.FC<Props> = ({
    totalAds,
    viewMode,
    isHybridView,
    handleToggleViewMode,
    handleOrderChange,
}) => {
    const { t, i18n } = useTranslation(['common']);

    const totalResultsTranslationParams = useMemo(() => {
        if (totalAds && totalAds >= adsLimitForMap) {
            return {
                total: `${adsLimitForMap}+`,
                plural: 's',
            };
        } else {
            return {
                total: String(Intl.NumberFormat(i18n.language).format(totalAds ?? 0)),
                plural: totalAds && totalAds > 1 ? 's' : '',
            };
        }
    }, [i18n.language, totalAds]);

    return (
        <Container>
            <Box display="flex" alignItems="center" flex="1">
                <StyledAdCountTypography data-target="ad-count" data-target-id="ad-count" noWrap>
                    {t('common:totalResults', totalResultsTranslationParams)}
                </StyledAdCountTypography>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center">
                <IconButton data-target-id="map-view-toggle" onClick={handleToggleViewMode}>
                    <Icon name={viewMode === 'list' ? 'MapOutlined' : 'MapFilled'} />
                </IconButton>
                <StyledListIconButton onClick={handleToggleViewMode} data-target-id="grid-view-toggle">
                    <Icon name={viewMode === 'list' ? 'GridFilled' : 'GridOutlined'} />
                </StyledListIconButton>
                {(isHybridView || viewMode === 'list') && (
                    <OrderBySelect
                        name="orderBy"
                        onChange={(event: ChangeEvent<{ value: unknown }>) => {
                            handleOrderChange(String(event.target.value));
                        }}
                    />
                )}
            </Box>
        </Container>
    );
};
